import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Users, Bot, TrendingUp, DollarSign, AlertTriangle,
  CheckCircle, Clock, RefreshCw, BarChart3, Globe, Activity,
  ChevronDown, FileText, Settings
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminGovernance() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [dashboard, setDashboard] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [navigate, period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, complianceRes] = await Promise.all([
        axios.get(`${API_URL}/api/governance/dashboard?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/governance/compliance/check`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDashboard(dashboardRes.data);
      setCompliance(complianceRes.data);
    } catch (error) {
      console.error('Error loading governance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  if (loading && !dashboard) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  const userMetrics = dashboard?.user_metrics || {};
  const subscriptionMetrics = dashboard?.subscription_metrics || {};
  const revenueMetrics = dashboard?.revenue_metrics || {};
  const aiUsageMetrics = dashboard?.ai_usage_metrics || {};
  const omniMetrics = dashboard?.omnichannel_metrics || {};

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-governance-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-indigo-600" />
              Centro de Gobernanza
            </h1>
            <p className="text-gray-500 text-sm">Control global del ecosistema UPIM IA</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="90d">Últimos 90 días</option>
              <option value="365d">Último año</option>
            </select>
            <button
              onClick={loadData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Compliance Alert Banner */}
        {compliance && compliance.alerts?.length > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-800">
                  {compliance.alerts.length} alerta(s) de cumplimiento
                </p>
                <p className="text-sm text-amber-700">
                  {compliance.alerts[0]?.message}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {[
            { id: 'overview', label: 'Resumen', icon: BarChart3 },
            { id: 'revenue', label: 'Ingresos', icon: DollarSign },
            { id: 'compliance', label: 'Cumplimiento', icon: CheckCircle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-xs text-green-600 font-medium">
                    +{userMetrics.user_growth_rate}%
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(userMetrics.total_users)}</p>
                <p className="text-sm text-gray-500">Usuarios totales</p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(revenueMetrics.mrr_mxn)}</p>
                <p className="text-sm text-gray-500">MRR (Ingresos mensuales)</p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Bot className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(aiUsageMetrics.total_messages)}</p>
                <p className="text-sm text-gray-500">Mensajes IA totales</p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span className="text-xs text-gray-500">
                    {subscriptionMetrics.conversion_rate}%
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{subscriptionMetrics.paid_subscribers}</p>
                <p className="text-sm text-gray-500">Suscriptores de pago</p>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Agent Distribution */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-500" />
                  Uso por Agente IA
                </h3>
                <div className="space-y-3">
                  {dashboard?.agent_distribution?.slice(0, 6).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700 capitalize">{item.agent}</span>
                          <span className="text-gray-500">{item.percentage}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-purple-500"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Geographic Distribution */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-red-500" />
                  Distribución Geográfica
                </h3>
                <div className="space-y-3">
                  {dashboard?.geographic_distribution?.slice(0, 6).map((item, idx) => {
                    const maxCount = Math.max(...(dashboard?.geographic_distribution?.map(s => s.count) || [1]));
                    const percentage = (item.count / maxCount) * 100;
                    
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700 capitalize">{item.state?.replace(/_/g, ' ')}</span>
                            <span className="text-gray-500">{item.count}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-red-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Subscription Metrics */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Métricas de Suscripción</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{subscriptionMetrics.trial_users}</p>
                  <p className="text-xs text-gray-500">Trial</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{subscriptionMetrics.premium_monthly}</p>
                  <p className="text-xs text-gray-500">Premium Mensual</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{subscriptionMetrics.premium_annual}</p>
                  <p className="text-xs text-gray-500">Premium Anual</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{subscriptionMetrics.paid_subscribers}</p>
                  <p className="text-xs text-gray-500">Total Pagados</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{subscriptionMetrics.conversion_rate}%</p>
                  <p className="text-xs text-gray-500">Tasa Conversión</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                <p className="text-sm opacity-80 mb-1">MRR (Mensual)</p>
                <p className="text-3xl font-bold">{formatCurrency(revenueMetrics.mrr_mxn)}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
                <p className="text-sm opacity-80 mb-1">ARR (Anual)</p>
                <p className="text-3xl font-bold">{formatCurrency(revenueMetrics.arr_mxn)}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-6 text-white">
                <p className="text-sm opacity-80 mb-1">ARPU (Por usuario)</p>
                <p className="text-3xl font-bold">{formatCurrency(revenueMetrics.arpu_mxn)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Desglose de Ingresos</h3>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-3">Concepto</th>
                    <th className="pb-3 text-right">Cantidad</th>
                    <th className="pb-3 text-right">Precio</th>
                    <th className="pb-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-3">Suscripción Premium Mensual</td>
                    <td className="py-3 text-right">{subscriptionMetrics.premium_monthly}</td>
                    <td className="py-3 text-right">$1,000</td>
                    <td className="py-3 text-right font-medium">{formatCurrency(subscriptionMetrics.premium_monthly * 1000)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3">Suscripción Premium Anual</td>
                    <td className="py-3 text-right">{subscriptionMetrics.premium_annual}</td>
                    <td className="py-3 text-right">$10,000/año</td>
                    <td className="py-3 text-right font-medium">{formatCurrency(subscriptionMetrics.premium_annual * 10000 / 12)}/mes</td>
                  </tr>
                  <tr className="font-bold">
                    <td className="py-3">Total MRR</td>
                    <td className="py-3"></td>
                    <td className="py-3"></td>
                    <td className="py-3 text-right text-green-600">{formatCurrency(revenueMetrics.mrr_mxn)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <div className="space-y-6">
            {/* Status Banner */}
            <div className={`rounded-xl p-6 ${
              compliance?.status === 'healthy' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <div className="flex items-center gap-4">
                {compliance?.status === 'healthy' ? (
                  <CheckCircle className="w-10 h-10 text-green-600" />
                ) : (
                  <AlertTriangle className="w-10 h-10 text-amber-600" />
                )}
                <div>
                  <h3 className={`text-lg font-bold ${
                    compliance?.status === 'healthy' ? 'text-green-800' : 'text-amber-800'
                  }`}>
                    {compliance?.status === 'healthy' ? 'Sistema Saludable' : 'Atención Requerida'}
                  </h3>
                  <p className={`text-sm ${
                    compliance?.status === 'healthy' ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    {compliance?.total_alerts || 0} alertas activas • Última verificación: {new Date(compliance?.check_time).toLocaleString('es-MX')}
                  </p>
                </div>
              </div>
            </div>

            {/* Alerts List */}
            {compliance?.alerts?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Alertas Activas</h3>
                <div className="space-y-3">
                  {compliance.alerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      alert.severity === 'high' ? 'bg-red-50 border-red-200' :
                      alert.severity === 'medium' ? 'bg-amber-50 border-amber-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                          alert.severity === 'high' ? 'text-red-600' :
                          alert.severity === 'medium' ? 'text-amber-600' :
                          'text-blue-600'
                        }`} />
                        <div>
                          <p className="font-medium text-gray-900">{alert.message}</p>
                          <p className="text-sm text-gray-600 mt-1">Acción: {alert.action_required}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Checks Performed */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Verificaciones Realizadas</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {compliance?.checks_performed?.map((check, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-gray-700 capitalize">{check.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
